/** @format */

import React from 'react'

import {CHECKBOX, FEATURE_DISPLAY_OVERRIDES, INFINITY} from './constants'

// Applies FEATURE_DISPLAY_OVERRIDES, returning a copy so the underlying payload
// is untouched. Call this anywhere a feature is rendered.
export function applyDisplayOverrides(feature) {
  const override = FEATURE_DISPLAY_OVERRIDES[feature.id]
  if (!override) return feature

  const displayed = {...feature}
  if (override.name) {
    displayed.name = override.name
  }
  if (override.shortname) {
    displayed.shortname = override.shortname
  }
  if (override.adjustLimit && typeof feature.limit === 'number') {
    displayed.limit = override.adjustLimit(feature.limit)
  }
  // A count of exactly one wants the singular. Only reachable where a limit is
  // attached — the comparison table's row labels carry no limit, so they keep
  // the plural regardless of what any single plan allows.
  if (override.singularName && displayed.limit === 1) {
    displayed.name = override.singularName
    displayed.shortname = override.singularShortname
  }
  if (override.anchor) {
    displayed.anchor = override.anchor
  }
  if (override.suffix) {
    displayed.suffix = override.suffix
  }
  if (override.prefix) {
    displayed.prefix = override.prefix
  }
  return displayed
}

export function isPlanInProduct(plan_id, prod) {
  return (
    -1 !==
    [prod.monthly, prod.yearly]
      .filter(x => x)
      .map(x => x.stripe_plan)
      .indexOf(plan_id)
  )
}

// The billing period that bills `planId`, or null wherever that isn't a single
// answer: `free` carries one plan id under both of its product's period keys,
// and a legacy or unknown plan is claimed by neither key of any product. Takes
// the full product list and counts only the available ones — the products
// PlansPage renders — so a period this returns always has a card to highlight.
export function billingPeriodForPlan(planId, products) {
  if (!planId) return null

  const claiming = ['monthly', 'yearly'].filter(period =>
    products.some(
      prod => prod.available && prod[period]?.stripe_plan === planId,
    ),
  )
  return claiming.length === 1 ? claiming[0] : null
}

export function getFeatureValueForProduct(featureId, product) {
  // Keyed by id, not name: names are display copy and are not unique
  // (monthly_submissions and account_monthly_submissions share one).
  const found = product.monthly.features.find(f => f.id === featureId) ?? null
  const feature = found && applyDisplayOverrides(found)
  const limit =
    feature && feature.limit !== undefined
      ? feature.limit === 'Unlimited'
        ? INFINITY
        : feature.limit !== 0
          ? feature.limit.toLocaleString() + (feature.units || '')
          : // A zero limit reads the same as not having the feature at all, so
            // leave the cell blank rather than marking it
            ' '
      : null
  return feature ? limit || CHECKBOX : <>&nbsp;</>
}

// A comparison-table row is either a feature or a plugin. Plugin rows carry a
// pluginKind and are available when the plan includes the feature named by
// `requires` — or on every plan, when a plugin names no requirement.
export function getRowValueForProduct(row, product) {
  if (row.pluginKind === undefined) {
    return getFeatureValueForProduct(row.id, product)
  }
  if (!row.requires) {
    return CHECKBOX
  }
  // Blank rather than a dash when unsupported, matching how the feature table
  // renders a feature a plan simply doesn't include. The dash is reserved for a
  // limit that exists but is zero.
  return product.monthly.features.some(f => f.id === row.requires) ? (
    CHECKBOX
  ) : (
    <>&nbsp;</>
  )
}

export function thousandsWithOneDecimal(num) {
  if (num === 'Unlimited') {
    return INFINITY
  } else if (num > 999) {
    return (Math.round(num / 100) / 10).toString() + 'K'
  } else {
    return num
  }
}

function scrollTo(selector, offset = 0) {
  if (window.matchMedia('(max-width: 768px)').matches) {
    return
  }
  const el = document.querySelector(selector)
  const top = el.getBoundingClientRect().top + window.pageYOffset
  window.scroll({
    top: top + offset,
    left: 0,
    behavior: 'smooth',
  })
}
